import { z } from 'zod';

const DOMAIN_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const DOMAIN_NAME_PATTERN =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const IPV4_PATTERN =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

const isReservedIp = (ip: string): boolean => {
  const parts = ip.split('.').map(Number);
  const [a, b, c, d] = parts;
  if (a === undefined || b === undefined || c === undefined || d === undefined)
    return true;

  if (a === 0) return true;
  if (a === 10) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  if (a === 127) return true;
  if (a === 169 && b === 254) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 0 && c === 0) return true;
  if (a === 192 && b === 0 && c === 2) return true;
  if (a === 192 && b === 88 && c === 99) return true;
  if (a === 192 && b === 168) return true;
  if (a === 198 && (b === 18 || b === 19)) return true;
  if (a === 198 && b === 51 && c === 100) return true;
  if (a === 203 && b === 0 && c === 113) return true;
  if (a >= 224) return true;
  if (a === 255 && b === 255 && c === 255 && d === 255) return true;

  return false;
};

export const DOMAIN_RESERVED_LABELS = [
  'www',
  'api',
  'admin',
  'mail',
  'ftp',
  'ns1',
  'ns2'
] as const;

const normalizeHostname = (value: string): string =>
  value.trim().toLowerCase().replace(/\.$/, '');

const parseBooleanEnvValue = (value: unknown): unknown => {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return value;

  const normalizedValue = value.trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalizedValue)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalizedValue)) return false;

  return value;
};

const booleanEnvSchema = z.preprocess(parseBooleanEnvValue, z.boolean());

const hostnameEnvSchema = z
  .string()
  .trim()
  .min(3)
  .max(253)
  .transform(normalizeHostname)
  .refine((value) => DOMAIN_NAME_PATTERN.test(value), {
    message: 'Invalid hostname.'
  });

export const targetIpSchema = z
  .string()
  .trim()
  .refine((value) => IPV4_PATTERN.test(value), {
    message: 'Target IP must be a valid IPv4 address.'
  })
  .refine((value) => !isReservedIp(value), {
    message: 'Target IP must be a public (non-reserved) IPv4 address.'
  });

const adminEmailsEnvSchema = z
  .string()
  .trim()
  .default('')
  .transform((value) =>
    value
      .split(',')
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email.length > 0)
  );

export const serverEnvSchema = z.object({
  PORT: z.coerce.number().int().min(1).max(65535).default(3000),
  POCKETBASE_URL: z.string().trim().url(),
  POCKETBASE_ADMIN_EMAIL: z.string().trim().email(),
  POCKETBASE_ADMIN_PASSWORD: z.string().min(8),
  CLOUDFLARE_API_TOKEN: z.string().trim().min(20),
  CLOUDFLARE_ZONE_ID: z.string().trim().min(1),
  DOMAIN_BASE: hostnameEnvSchema,
  DOMAIN_MAX_PER_USER: z.coerce.number().int().min(1).max(10).default(3),
  ADMIN_EMAILS: adminEmailsEnvSchema,
  HEALTH_CHECK_ENABLED: booleanEnvSchema.default(true),
  HEALTH_CHECK_INTERVAL_MINUTES: z.coerce
    .number()
    .int()
    .min(1)
    .max(1440)
    .default(60),
  HEALTH_CHECK_TIMEOUT_MS: z.coerce
    .number()
    .int()
    .min(1000)
    .max(60_000)
    .default(10_000),
  // A domain unreachable for this many consecutive days is deleted.
  HEALTH_CHECK_GRACE_DAYS: z.coerce.number().int().min(1).max(365).default(2),
  // Logs what would be deleted without deleting it. Worth one sweep before
  // arming the job against real domains.
  HEALTH_CHECK_DRY_RUN: booleanEnvSchema.default(false)
});

export const clientEnvSchema = z.object({
  BUN_PUBLIC_POCKETBASE_URL: z.string().trim().url()
});

const rawDomainInputSchema = z
  .string()
  .trim()
  .min(1)
  .max(253)
  .transform(normalizeHostname);

export const authCredentialsSchema = z.object({
  email: z.string().trim().email(),
  password: z.string().min(8).max(72)
});

export const createDomainBodySchema = z.object({
  domain: rawDomainInputSchema,
  targetIp: targetIpSchema,
  proxied: booleanEnvSchema
});

export const updateDomainSettingsBodySchema = z.object({
  targetIp: targetIpSchema,
  proxied: z.boolean()
});

export const domainIdParamSchema = z.object({
  id: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z0-9]+$/, 'Invalid domain ID.')
});

export const normalizeDomainInput = (
  value: string,
  baseDomain: string,
  reservedLabels: readonly string[] = DOMAIN_RESERVED_LABELS
): { subdomain: string; fqdn: string } | undefined => {
  const normalizedBaseDomain = normalizeHostname(baseDomain);
  const normalizedInput = normalizeHostname(value);
  if (!normalizedInput || normalizedInput === normalizedBaseDomain)
    return undefined;

  const baseDomainSuffix = `.${normalizedBaseDomain}`;
  let subdomain = normalizedInput;

  if (normalizedInput.includes('.')) {
    if (!normalizedInput.endsWith(baseDomainSuffix)) return undefined;
    subdomain = normalizedInput.slice(0, -baseDomainSuffix.length);
  }

  if (!subdomain || subdomain.includes('.')) return undefined;
  if (!DOMAIN_LABEL_PATTERN.test(subdomain)) return undefined;

  const normalizedReservedLabels = new Set(
    reservedLabels.map((label) => normalizeHostname(label))
  );
  if (normalizedReservedLabels.has(subdomain)) return undefined;

  const fqdn = `${subdomain}.${normalizedBaseDomain}`;
  if (!DOMAIN_NAME_PATTERN.test(fqdn)) return undefined;

  return { subdomain, fqdn };
};

export const createClaimSchema = (
  baseDomain: string,
  reservedLabels: readonly string[] = DOMAIN_RESERVED_LABELS
) =>
  z.object({
    domain: rawDomainInputSchema.transform((value, context) => {
      const normalizedDomain = normalizeDomainInput(
        value,
        baseDomain,
        reservedLabels
      );

      if (!normalizedDomain) {
        context.addIssue({
          code: 'custom',
          message: `Domain must be a single valid subdomain under ${normalizeHostname(baseDomain)}.`
        });
        return z.NEVER;
      }

      return normalizedDomain;
    })
  });

export const authenticatedUserSchema = z.object({
  id: z.string().min(1),
  email: z
    .union([z.string().email(), z.literal('')])
    .optional()
    .default(''),
  name: z.string().optional()
});

// Shape of a Sharkord server's GET /info. This is an untrusted response from a
// server the domain owner controls and whose payload varies between versions,
// so every field is optional, bounded, and independently `.catch`-ed: one odd
// value degrades that field to undefined instead of discarding the snapshot.
// `logo` is deliberately absent -- it is a string on some servers and a file
// object on others, and nothing renders it, so unknown-key stripping drops it.
export const serverInfoSchema = z.object({
  serverId: z.string().max(100).optional().catch(undefined),
  version: z.string().max(50).optional().catch(undefined),
  name: z.string().max(200).optional().catch(undefined),
  description: z.string().max(2000).optional().catch(undefined),
  allowNewUsers: z.boolean().optional().catch(undefined)
});

export const domainSchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  subdomain: z.string().regex(DOMAIN_LABEL_PATTERN),
  fqdn: z.string().regex(DOMAIN_NAME_PATTERN),
  targetIp: targetIpSchema,
  proxied: z.boolean(),
  cloudflareRecordId: z.string().min(1),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
  // Absent until the health monitor has seen the origin respond at least once.
  // Doubles as the anchor for the consecutive-downtime clock.
  lastSeenUpAt: z.string().datetime({ offset: true }).optional(),
  // Last successful /info payload, refreshed on each passing health check.
  serverInfo: serverInfoSchema.optional()
});

export const publicDomainSchema = domainSchema.omit({
  userId: true,
  cloudflareRecordId: true
});

export const adminDomainSchema = domainSchema.extend({
  owner: z.object({
    id: z.string().min(1),
    email: z
      .union([z.string().email(), z.literal('')])
      .optional()
      .default(''),
    name: z.string().optional()
  })
});

export const domainConfigSchema = z.object({
  baseDomain: hostnameEnvSchema,
  maxPerUser: z.number().int().min(1),
  // Surfaced so the UI can show how long a domain has left before the health
  // monitor deletes it, and how stale a reading is allowed to be before the
  // domain is presented as down.
  healthCheckGraceDays: z.number().int().min(1),
  healthCheckIntervalMinutes: z.number().int().min(1)
});

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fields: z.record(z.string(), z.array(z.string())).optional()
  })
});

export const domainResponseSchema = z.object({
  data: publicDomainSchema
});

export const domainsResponseSchema = z.object({
  data: z.array(publicDomainSchema)
});

export const adminDomainsResponseSchema = z.object({
  data: z.array(adminDomainSchema)
});

export const configResponseSchema = z.object({
  data: domainConfigSchema
});

// isAdmin is resolved server-side from ADMIN_EMAILS, so the client has to ask
// rather than read a flag off its own PocketBase record.
export const currentUserSchema = authenticatedUserSchema.extend({
  isAdmin: z.boolean()
});

export const currentUserResponseSchema = z.object({
  data: currentUserSchema
});
